import { formatPriceCurrency, getCurrency } from 'utils/utils';
import {
  FlProposal,
  HotProposal,
  ProductProposal,
  Proposal,
  ReportsList,
  TransferProposal,
  TripSlotRequirement,
} from 'ts/shared/interfaces/CreateProposalParams';
import { ProposalData, ProposalHotel } from 'ts/shared/interfaces/ProposalData';
import {
  getAfterFlightData,
  getBeforeFlightData,
  getFlightLegs,
  getTransferData,
  matchesFlightAfterSlot,
  matchesFlightBeforeSlot,
  matchesFlightMiddleTransition,
  matchesHotelSlot,
  matchesTransferSlot,
  toDayKey,
  tripRequirementToPType,
  tripRowKeys,
  TripSlotContext,
} from './tripBuilder';
import { applyClaudeHotelInfoConfigToExternalParam } from './claudeProposalInformation';
import { applyHotelInfoConfigToExternalParam } from './hotelProposalInformation';
import { getReportList } from './proposal';
import {
  applyHotelDataDraftToProposalHotel,
  isHotelDataConfigOnlyPayload,
  mapFlightCard,
  mapTransferCard,
  parseProposalDataProducts,
} from './tripResultBuilderPreview';
import type { TripBuilderHotelDataDraftPayload } from './tripResultBuilderPreview';
import type {
  TripPreviewChainNode,
  TripPreviewFlightCard,
  TripPreviewHotelCard,
  TripPreviewSection,
  TripPreviewSlotView,
  TripPreviewTransferCard,
} from './tripResultBuilderPreview';
import { getProposal, saveProposal } from './idbUrl';
export type TripBuilderIdbSlotNode = {
  city: string;
  checkIn: string;
  checkOut: string;
  slotKey: string;
};

const idbCollection = <T>(getKey: (item: T, index: number) => string) => ({
  findByKey: (items: T[], cardKey: string) =>
    items.find((item, index) => getKey(item, index) === cardKey),
  removeByKey: (items: T[], cardKey: string) =>
    items.filter((item, index) => getKey(item, index) !== cardKey),
  updateByKey: (items: T[], cardKey: string, updater: (item: T) => T) => {
    const itemIndex = items.findIndex((item, index) => getKey(item, index) === cardKey);
    if (itemIndex < 0) {
      return items;
    }
    return items.map((item, index) => (index === itemIndex ? updater(item) : item));
  },
});

export const getIdbHotelCardKey = (hotel: HotProposal, index: number) =>
  `idb-htl-${hotel.item?.htuKey || hotel.item?.itemCode || hotel.searchToken || index}`;

const idbHotels = idbCollection(getIdbHotelCardKey);
export const findIdbHotelByCardKey = idbHotels.findByKey;
export const removeIdbHotelByCardKey = idbHotels.removeByKey;
export const updateIdbHotelByCardKey = idbHotels.updateByKey;

const getSourceSerp = (hotel: HotProposal) =>
  hotel.googleData?.serpResults?.serpHotel || hotel.serpHotel;

const getSourceTripadvisor = (hotel: HotProposal) =>
  hotel.tripAdvisorData?.tripAdvisorHotel || hotel.tripAdvisorHotel;

export const mapHotProposalToModalHotel = (hotel: HotProposal): ProposalHotel => {
  const item = hotel.item;
  const room = item?.roomClasses?.[0];
  const sourceSerp = getSourceSerp(hotel);
  const sourceTripadvisor = getSourceTripadvisor(hotel);

  return {
    hotel: {
      code: item?.itemCode || '',
      type: null,
      name: item?.item?.name || '',
    },
    localName: item?.item?.name || '',
    city: {
      code: '',
      type: null,
      name: item?.info?.cityName || item?.addressInfo?.city || '',
    },
    codes: item?.item?.bpid ? [{ name: 'BPID', code: item.item.bpid }] : [],
    facilities: [],
    listOfImages: [],
    remarks: null,
    adress: item?.addressInfo?.address || '',
    location: null,
    zip: item?.addressInfo?.zip || '',
    email: item?.addressInfo?.email || null,
    phone: item?.addressInfo?.phone || null,
    hotelURL: null,
    country: {
      code: null,
      type: null,
      name: item?.addressInfo?.country || '',
    },
    star: item?.item?.star || 0,
    geoLocation: {
      latitude: String(item?.position?.latitude ?? ''),
      longitude: String(item?.position?.longitude ?? ''),
      pip: '',
    },
    rooms: [
      {
        pax: {
          adults: hotel.adults || room?.hotelRooms?.[0]?.numAdt || 0,
          children: hotel.children || room?.hotelRooms?.[0]?.numCnn || 0,
          senior: 0,
          youth: 0,
          infant: 0,
          childrenAges: null,
        },
        room: {
          code: '',
          type: null,
          name: room?.hotelRooms?.[0]?.displayName || '',
        },
        board: {
          code: room?.board?.basis?.code || '',
          type: null,
          name: room?.board?.basis?.desc || '',
        },
        startDate: hotel.checkIn || room?.checkIn || '',
        nights: room?.nights || 0,
        endtDate: hotel.checkOut || room?.checkOut || '',
        amenities: null,
        supplements: [],
        remarks: [],
        cxl: '',
        status: { code: '', type: null, name: '' },
      },
    ],
    proposalItem: item,
    serpHotels: null,
    sportActivities: null,
    tripAdvisorHotel: hotel.tripAdvisorHotel || sourceTripadvisor,
    serpHotelEntire: sourceSerp,
    tripAdvisorHotelEntire: sourceTripadvisor,
    serpHotel: hotel.serpHotel || sourceSerp,
    hotelInfoConfig: hotel.hotelInfoConfig,
    claudeHotelInfoConfig: hotel.claudeHotelInfoConfig,
    googleChecked: hotel.googleChecked,
    tripAdvisorChecked: hotel.tripAdvisorChecked,
    hotelInformatiom: hotel.hotelInformation,
    aI_HotelInformatiom: hotel.claudeData,
    supplier: { code: '', type: '', name: '' },
    searchParameters: null,
  };
};

export const mapIdbHotelCard = (hotel: HotProposal, index: number): TripPreviewHotelCard => {
  return {
    key: getIdbHotelCardKey(hotel, index),
    name: hotel.item?.item?.name || hotel.item?.info?.cityName || 'Hotel',
    star: hotel.item?.item?.star,
    priceLabel: hotel.price
      ? formatPriceCurrency(hotel.price, getCurrency(hotel.currency || hotel.item?.currency))
      : null,
    isActive: false,
    logoCode: hotel.item?.info?.picture,
    itemCode: hotel.item?.itemCode,
    hotelIndex: index,
    bpid: hotel.item?.item?.bpid,
  };
};

export const getDraftHotelsForSlot = (
  idbHotels: HotProposal[],
  city: string,
  checkIn: string,
  checkOut: string,
  existing: TripPreviewHotelCard[],
): TripPreviewHotelCard[] => {
  if (!idbHotels.length) {
    return [];
  }
  const existingCodes = new Set(existing.map((card) => card.itemCode).filter(Boolean));
  return idbHotels
    .filter((hotel) => matchesHotelSlot(hotel, city, checkIn, checkOut))
    .filter((hotel) => !hotel.item?.itemCode || !existingCodes.has(hotel.item.itemCode))
    .map(mapIdbHotelCard);
};

export const normalizeIdbHotelForReport = (hotel: HotProposal): HotProposal => {
  const item = hotel.item;
  const choosenRoomIds = item?.choosenRoomIds?.length
    ? item.choosenRoomIds
    : item?.choosenRoomId
      ? [item.choosenRoomId]
      : item?.roomClasses?.[0]?.rcUniqueKey
        ? [item.roomClasses[0].rcUniqueKey]
        : [];

  const sourceSerp = getSourceSerp(hotel);
  const sourceTripadvisor = getSourceTripadvisor(hotel);

  return {
    ...hotel,
    item: {
      ...item,
      choosenRoomIds,
    },
    googleData: sourceSerp
      ? ({
          ...(hotel.googleData || {}),
          serpResults: {
            ...((hotel.googleData as any)?.serpResults || {}),
            serpHotel: sourceSerp,
          },
        } as HotProposal['googleData'])
      : hotel.googleData,
    tripAdvisorData: sourceTripadvisor
      ? ({
          ...(hotel.tripAdvisorData || {}),
          tripAdvisorHotel: sourceTripadvisor,
        } as HotProposal['tripAdvisorData'])
      : hotel.tripAdvisorData,
    googleChecked:
      hotel.googleChecked ||
      (hotel.serpHotel
        ? {
            reviews: !!hotel.serpHotel?.reviews,
            imagesIndexes: (sourceSerp?.images || []).reduce(
              (acc: number[], img: any, index: number) => {
                if (
                  (hotel.serpHotel?.images || []).some(
                    (selected: any) => selected?.originalImage === img?.originalImage,
                  )
                ) {
                  acc.push(index);
                }
                return acc;
              },
              [],
            ),
          }
        : hotel.googleChecked),
    tripAdvisorChecked:
      hotel.tripAdvisorChecked ||
      (hotel.tripAdvisorHotel
        ? {
            details: !!hotel.tripAdvisorHotel?.description,
            reviews: !!hotel.tripAdvisorHotel?.numReviews,
            amenities: !!hotel.tripAdvisorHotel?.amenities?.length,
          }
        : hotel.tripAdvisorChecked),
  };
};

export const applyDraftPayloadToIdbHotel = (
  hotel: HotProposal,
  payload: TripBuilderHotelDataDraftPayload,
): HotProposal => {
  if (isHotelDataConfigOnlyPayload(payload)) {
    return {
      ...hotel,
      ...(payload.hotelInfoConfig ? { hotelInfoConfig: payload.hotelInfoConfig } : {}),
      ...(payload.claudeHotelInfoConfig
        ? { claudeHotelInfoConfig: payload.claudeHotelInfoConfig }
        : {}),
    };
  }

  const modalHotel = mapHotProposalToModalHotel(hotel);
  const { serpHotel, tripAdvisorHotel } = applyHotelDataDraftToProposalHotel(modalHotel, payload);

  return {
    ...hotel,
    serpHotel,
    tripAdvisorHotel,
    ...(payload.googleChecked
      ? {
          googleChecked: {
            imagesIndexes: payload.googleChecked.imagesIndexes,
            reviews: payload.googleChecked.reviews,
          },
        }
      : {}),
    ...(payload.tripAdvisorChecked
      ? {
          tripAdvisorChecked: {
            details: payload.tripAdvisorChecked.details,
            reviews: payload.tripAdvisorChecked.reviews,
            amenities: payload.tripAdvisorChecked.amenities,
          },
        }
      : {}),
    ...(payload.sourceSerpHotel !== undefined
      ? {
          googleData: {
            ...(hotel.googleData || {}),
            serpResults: {
              ...((hotel.googleData as any)?.serpResults || {}),
              serpHotel: payload.sourceSerpHotel,
            },
          } as HotProposal['googleData'],
        }
      : {}),
    ...(payload.sourceTripadvisorHotel !== undefined
      ? {
          tripAdvisorData: {
            ...(hotel.tripAdvisorData || {}),
            tripAdvisorHotel: payload.sourceTripadvisorHotel,
          } as HotProposal['tripAdvisorData'],
        }
      : {}),
  };
};

export const getIdbHotelExternalParam = (
  hotel: HotProposal | undefined,
  fallbackExternalParam?: string,
) => {
  if (!hotel) {
    return fallbackExternalParam;
  }
  const modalHotel = mapHotProposalToModalHotel(hotel);
  let param = fallbackExternalParam;
  if (hotel.hotelInfoConfig) {
    param = applyHotelInfoConfigToExternalParam(param, modalHotel, hotel.hotelInfoConfig);
  }
  if (hotel.claudeHotelInfoConfig) {
    param = applyClaudeHotelInfoConfigToExternalParam(
      param,
      modalHotel,
      hotel.claudeHotelInfoConfig,
    );
  }
  return param;
};

export const getSlotNodesFromModel = (slots: TripPreviewSlotView[]): TripBuilderIdbSlotNode[] =>
  slots.flatMap((slot) =>
    slot.chainNodes.map((node) => ({
      city: node.city,
      checkIn: node.checkIn,
      checkOut: node.checkOut,
      slotKey: node.slotKey,
    })),
  );

export const getExistingHotelItemCodes = (slots: TripPreviewSlotView[]) =>
  new Set(
    slots
      .flatMap((slot) => slot.chainNodes)
      .flatMap((node) => node.hotelSection?.hotels || [])
      .map((card) => card.itemCode)
      .filter(Boolean) as string[],
  );

export const getIdbHotelsToAdd = (
  idbHotels: HotProposal[],
  slots: TripPreviewSlotView[],
  proposalData?: ProposalData,
): HotProposal[] => {
  const existingItemCodes = getExistingHotelItemCodes(slots);
  if (proposalData) {
    parseProposalDataProducts(proposalData).hotels.forEach((hotel) => {
      const code = hotel.item?.itemCode;
      if (code) {
        existingItemCodes.add(code);
      }
    });
  }
  const slotNodes = getSlotNodesFromModel(slots);

  return idbHotels
    .filter((hotel) => !hotel.item?.itemCode || !existingItemCodes.has(hotel.item.itemCode))
    .filter((hotel) =>
      slotNodes.some((node) => matchesHotelSlot(hotel, node.city, node.checkIn, node.checkOut)),
    )
    .map(normalizeIdbHotelForReport);
};

export const applyIdbHotelConfigsToInputData = (
  inputData: string,
  hotels: HotProposal[],
): string => {
  let next = inputData;
  hotels.forEach((hotel) => {
    const modalHotel = mapHotProposalToModalHotel(hotel);
    if (hotel.hotelInfoConfig) {
      next = applyHotelInfoConfigToExternalParam(next, modalHotel, hotel.hotelInfoConfig);
    }
    if (hotel.claudeHotelInfoConfig) {
      next = applyClaudeHotelInfoConfigToExternalParam(
        next,
        modalHotel,
        hotel.claudeHotelInfoConfig,
      );
    }
  });
  return next;
};

export const buildIdbHotelsAddReports = (
  hotelsToAdd: HotProposal[],
  slots: TripPreviewSlotView[],
  options: {
    requirementByRow: Record<string, TripSlotRequirement>;
    hotelImageBySlot: Record<string, string>;
    showCancellationDate: boolean;
    cancellationDateDelta: number;
    language?: string;
  },
): ReportsList[] => {
  if (!hotelsToAdd.length) {
    return [];
  }

  const slotNodes = getSlotNodesFromModel(slots);
  const hotelSlotKeyBySearchToken: Record<string, string> = {};
  const pTypeBySearchToken: Record<string, NonNullable<ProductProposal['pType']>> = {};

  hotelsToAdd.forEach((hotel) => {
    const matchedSlot = slotNodes.find((node) =>
      matchesHotelSlot(hotel, node.city, node.checkIn, node.checkOut),
    );
    if (!matchedSlot) {
      return;
    }
    hotelSlotKeyBySearchToken[hotel.searchToken] = matchedSlot.slotKey;
    pTypeBySearchToken[hotel.searchToken] = tripRequirementToPType(
      options.requirementByRow[tripRowKeys.hotel(matchedSlot.slotKey)] ?? 'mandatory',
    );
  });

  return getReportList(
    {
      hotels: hotelsToAdd,
      flights: [],
      dynamic: [],
      ilPackages: [],
      transfers: [],
    } as Proposal,
    {
      pTypeBySearchToken,
      hotelSlotKeyBySearchToken,
      hotelImageBySlot: options.hotelImageBySlot,
      showCancellationDate: options.showCancellationDate,
      cancellationDateDelta: options.cancellationDateDelta,
      language: options.language,
    },
  );
};

export const persistIdbProposalHotels = async (
  nextHotels: HotProposal[],
  fallbackDraft?: Proposal,
) => {
  const draft = (await getProposal('proposal')) ||
    fallbackDraft || {
      hotels: [],
      flights: [],
      dynamic: [],
      ilPackages: [],
      transfers: [],
    };
  await saveProposal('proposal', { ...draft, hotels: nextHotels });
  return nextHotels;
};

export const loadIdbProposalHotels = async (): Promise<HotProposal[]> => {
  const draft = await getProposal('proposal');
  return draft?.hotels || [];
};

export const getIdbFlightCardKey = (flight: FlProposal, index: number) =>
  `idb-flt-${flight.item?.id || flight.searchToken || index}`;

const idbFlights = idbCollection(getIdbFlightCardKey);
export const findIdbFlightByCardKey = idbFlights.findByKey;
export const removeIdbFlightByCardKey = idbFlights.removeByKey;

const toIdbFlightCandidate = (
  flight: FlProposal,
  index: number,
  segment: 'before' | 'middle' | 'after',
  route?: string,
): TripSlotContext['beforeCandidates'][number] => {
  const beforeData = getBeforeFlightData(flight);
  const afterData = getAfterFlightData(flight);
  const legs = getFlightLegs(flight);
  const resolvedRoute =
    route ||
    (segment === 'after'
      ? afterData.route
      : segment === 'middle'
        ? legs.find((leg: any) => leg.route)?.route || beforeData.route
        : beforeData.route);
  return {
    flight,
    index,
    route: resolvedRoute,
    isRoundTrip: beforeData.isRoundTrip,
    airlineCode:
      segment === 'after'
        ? afterData.airlineCode || beforeData.airlineCode
        : segment === 'middle'
          ? legs.find((leg: any) => leg.route === resolvedRoute)?.airlineCode ||
            beforeData.airlineCode
          : beforeData.airlineCode,
  };
};

export const mapIdbFlightCard = (
  flight: FlProposal,
  index: number,
  segment: 'before' | 'middle' | 'after',
  route?: string,
): TripPreviewFlightCard =>
  mapFlightCard(toIdbFlightCandidate(flight, index, segment, route), {
    key: getIdbFlightCardKey(flight, index),
    segment,
    forceShowPrice: true,
  });

const isExistingFlightCard = (flight: FlProposal, existing: TripPreviewFlightCard[]) => {
  const flightId = flight.item?.id;
  const searchToken = flight.searchToken;
  return existing.some(
    (card) =>
      (!!flightId && card.flightId === flightId) ||
      (!!searchToken && card.searchToken === searchToken) ||
      card.key === getIdbFlightCardKey(flight, 0),
  );
};

const getDraftFlights = (
  idbFlightsList: FlProposal[],
  existing: TripPreviewFlightCard[],
  segment: 'before' | 'middle' | 'after',
  matches: (flight: FlProposal) => boolean,
  getRoute?: (flight: FlProposal) => string | undefined,
): TripPreviewFlightCard[] => {
  if (!idbFlightsList.length) {
    return [];
  }
  return idbFlightsList
    .filter(matches)
    .filter((flight) => !isExistingFlightCard(flight, existing))
    .map((flight, index) => mapIdbFlightCard(flight, index, segment, getRoute?.(flight)));
};

export const getDraftFlightsForBefore = (
  idbFlightsList: FlProposal[],
  checkIn: string,
  city: string,
  existing: TripPreviewFlightCard[],
): TripPreviewFlightCard[] =>
  getDraftFlights(idbFlightsList, existing, 'before', (flight) =>
    matchesFlightBeforeSlot(flight, checkIn, city),
  );

export const getDraftFlightsForAfter = (
  idbFlightsList: FlProposal[],
  checkOut: string,
  city: string,
  existing: TripPreviewFlightCard[],
): TripPreviewFlightCard[] =>
  getDraftFlights(idbFlightsList, existing, 'after', (flight) =>
    matchesFlightAfterSlot(flight, checkOut, city),
  );

export const getDraftFlightsForMiddle = (
  idbFlightsList: FlProposal[],
  transitionDay: string,
  fromCity: string,
  toCity: string,
  existing: TripPreviewFlightCard[],
): TripPreviewFlightCard[] =>
  getDraftFlights(
    idbFlightsList,
    existing,
    'middle',
    (flight) => matchesFlightMiddleTransition(flight, transitionDay, fromCity, toCity),
    (flight) =>
      getFlightLegs(flight).find((leg: any) => leg.departureDay === toDayKey(transitionDay || ''))
        ?.route,
  );

export const getExistingFlightIds = (slots: TripPreviewSlotView[]) => {
  const ids = new Set<string>();
  const tokens = new Set<string>();
  slots.forEach((slot) => {
    const collect = (cards: TripPreviewFlightCard[]) => {
      cards.forEach((card) => {
        if (card.flightId) {
          ids.add(card.flightId);
        }
        if (card.searchToken) {
          tokens.add(card.searchToken);
        }
      });
    };
    if (slot.beforeSection) {
      collect(slot.beforeSection.flights);
    }
    if (slot.afterSection) {
      collect(slot.afterSection.flights);
    }
    slot.chainNodes.forEach((node) => {
      node.middleSections.forEach((section) => collect(section.flights));
    });
  });
  return { ids, tokens };
};

export type TripBuilderIdbFlightMatch = {
  flight: FlProposal;
  segment: 'before' | 'middle' | 'after';
  rootSlotKey: string;
  transitionKey?: string;
};

export const getIdbFlightsToAdd = (
  idbFlights: FlProposal[],
  slots: TripPreviewSlotView[],
  proposalData?: ProposalData,
): TripBuilderIdbFlightMatch[] => {
  const { ids, tokens } = getExistingFlightIds(slots);
  if (proposalData) {
    parseProposalDataProducts(proposalData).flights.forEach((flight) => {
      if (flight.item?.id) {
        ids.add(flight.item.id);
      }
      if (flight.searchToken) {
        tokens.add(flight.searchToken);
      }
    });
  }
  const matched: TripBuilderIdbFlightMatch[] = [];
  const used = new Set<string>();

  const mark = (flight: FlProposal) => {
    const key = flight.item?.id || flight.searchToken;
    if (!key || used.has(key)) {
      return false;
    }
    used.add(key);
    return true;
  };

  const isNew = (flight: FlProposal) =>
    !(flight.item?.id && ids.has(flight.item.id)) &&
    !(flight.searchToken && tokens.has(flight.searchToken));

  slots.forEach((slot) => {
    const firstNode = slot.chainNodes[0];
    const lastNode = slot.chainNodes[slot.chainNodes.length - 1];
    if (!firstNode || !lastNode) {
      return;
    }

    idbFlights.filter(isNew).forEach((flight) => {
      if (matchesFlightBeforeSlot(flight, firstNode.checkIn, firstNode.city) && mark(flight)) {
        matched.push({ flight, segment: 'before', rootSlotKey: slot.rootSlotKey });
        return;
      }
      for (let index = 0; index < slot.chainNodes.length - 1; index += 1) {
        const node = slot.chainNodes[index];
        const nextNode = slot.chainNodes[index + 1];
        if (
          matchesFlightMiddleTransition(flight, nextNode.checkIn, node.city, nextNode.city) &&
          mark(flight)
        ) {
          matched.push({
            flight,
            segment: 'middle',
            rootSlotKey: slot.rootSlotKey,
            transitionKey: `${node.slotKey}|${nextNode.slotKey}`,
          });
          return;
        }
      }
      if (matchesFlightAfterSlot(flight, lastNode.checkOut, lastNode.city) && mark(flight)) {
        matched.push({ flight, segment: 'after', rootSlotKey: slot.rootSlotKey });
      }
    });
  });

  return matched;
};

export const buildIdbFlightsAddReports = (
  flightsToAdd: TripBuilderIdbFlightMatch[],
  options: {
    requirementByRow: Record<string, TripSlotRequirement>;
  },
): ReportsList[] => {
  if (!flightsToAdd.length) {
    return [];
  }

  const pTypeBySearchToken: Record<string, NonNullable<ProductProposal['pType']>> = {};
  flightsToAdd.forEach(({ flight, segment, rootSlotKey, transitionKey }) => {
    const rowKey =
      segment === 'before'
        ? tripRowKeys.before(rootSlotKey)
        : segment === 'after'
          ? tripRowKeys.after(rootSlotKey)
          : tripRowKeys.middle(transitionKey || '');
    pTypeBySearchToken[flight.searchToken] = tripRequirementToPType(
      options.requirementByRow[rowKey] ?? (segment === 'middle' ? 'optional' : 'mandatory'),
    );
  });

  return getReportList(
    {
      hotels: [],
      flights: flightsToAdd.map((item) => item.flight),
      dynamic: [],
      ilPackages: [],
      transfers: [],
    } as Proposal,
    { pTypeBySearchToken },
  );
};

export const persistIdbProposalFlights = async (
  nextFlights: FlProposal[],
  fallbackDraft?: Proposal,
) => {
  const draft = (await getProposal('proposal')) ||
    fallbackDraft || {
      hotels: [],
      flights: [],
      dynamic: [],
      ilPackages: [],
      transfers: [],
    };
  await saveProposal('proposal', { ...draft, flights: nextFlights });
  return nextFlights;
};

export const loadIdbProposalFlights = async (): Promise<FlProposal[]> => {
  const draft = await getProposal('proposal');
  return draft?.flights || [];
};

export const getIdbTransferCardKey = (transfer: TransferProposal, index: number) =>
  `idb-trn-${transfer.forwardItem?.priceKey || transfer.searchToken || index}`;

const idbTransfers = idbCollection(getIdbTransferCardKey);
export const findIdbTransferByCardKey = idbTransfers.findByKey;
export const removeIdbTransferByCardKey = idbTransfers.removeByKey;

export const mapIdbTransferCard = (
  transfer: TransferProposal,
  index: number,
): TripPreviewTransferCard =>
  mapTransferCard(getTransferData(transfer, index), {
    key: getIdbTransferCardKey(transfer, index),
  });

const isExistingTransferCard = (
  transfer: TransferProposal,
  existing: TripPreviewTransferCard[],
) => {
  const priceKey = transfer.forwardItem?.priceKey;
  const searchToken = transfer.searchToken;
  return existing.some(
    (card) =>
      (!!priceKey && card.priceKey === priceKey) ||
      (!!searchToken && card.searchToken === searchToken),
  );
};

export const getDraftTransfersForSlot = (
  idbTransfersList: TransferProposal[],
  city: string,
  checkIn: string,
  checkOut: string,
  existing: TripPreviewTransferCard[],
): TripPreviewTransferCard[] => {
  if (!idbTransfersList.length) {
    return [];
  }
  return idbTransfersList
    .filter((transfer) => matchesTransferSlot(transfer, city, checkIn, checkOut))
    .filter((transfer) => !isExistingTransferCard(transfer, existing))
    .map(mapIdbTransferCard);
};

export const getExistingTransferKeys = (slots: TripPreviewSlotView[]) => {
  const priceKeys = new Set<string>();
  const tokens = new Set<string>();
  slots.forEach((slot) => {
    slot.chainNodes.forEach((node) => {
      (node.transferSection?.transfers || []).forEach((card) => {
        if (card.priceKey) {
          priceKeys.add(card.priceKey);
        }
        if (card.searchToken) {
          tokens.add(card.searchToken);
        }
      });
    });
  });
  return { priceKeys, tokens };
};

export type TripBuilderIdbTransferMatch = {
  transfer: TransferProposal;
  slotKey: string;
};

export const getIdbTransfersToAdd = (
  idbTransfers: TransferProposal[],
  slots: TripPreviewSlotView[],
  proposalData?: ProposalData,
): TripBuilderIdbTransferMatch[] => {
  const { priceKeys, tokens } = getExistingTransferKeys(slots);
  if (proposalData) {
    parseProposalDataProducts(proposalData).transfers.forEach((transfer) => {
      if (transfer.forwardItem?.priceKey) {
        priceKeys.add(transfer.forwardItem.priceKey);
      }
      if (transfer.searchToken) {
        tokens.add(transfer.searchToken);
      }
    });
  }
  const slotNodes = getSlotNodesFromModel(slots);
  const matched: TripBuilderIdbTransferMatch[] = [];
  const used = new Set<string>();

  idbTransfers.forEach((transfer) => {
    const identity = transfer.forwardItem?.priceKey || transfer.searchToken;
    if (!identity || used.has(identity)) {
      return;
    }
    if (
      (transfer.forwardItem?.priceKey && priceKeys.has(transfer.forwardItem.priceKey)) ||
      (transfer.searchToken && tokens.has(transfer.searchToken))
    ) {
      return;
    }
    const matchedSlot = slotNodes.find((node) =>
      matchesTransferSlot(transfer, node.city, node.checkIn, node.checkOut),
    );
    if (!matchedSlot) {
      return;
    }
    used.add(identity);
    matched.push({ transfer, slotKey: matchedSlot.slotKey });
  });

  return matched;
};

export const buildIdbTransfersAddReports = (
  transfersToAdd: TripBuilderIdbTransferMatch[],
  options: {
    requirementByRow: Record<string, TripSlotRequirement>;
  },
): ReportsList[] => {
  if (!transfersToAdd.length) {
    return [];
  }

  const pTypeBySearchToken: Record<string, NonNullable<ProductProposal['pType']>> = {};
  transfersToAdd.forEach(({ transfer, slotKey }) => {
    pTypeBySearchToken[transfer.searchToken] = tripRequirementToPType(
      options.requirementByRow[tripRowKeys.transfer(slotKey)] ?? 'mandatory',
    );
  });

  return getReportList(
    {
      hotels: [],
      flights: [],
      dynamic: [],
      ilPackages: [],
      transfers: transfersToAdd.map((item) => item.transfer),
    } as Proposal,
    { pTypeBySearchToken },
  );
};

export const persistIdbProposalTransfers = async (
  nextTransfers: TransferProposal[],
  fallbackDraft?: Proposal,
) => {
  const draft = (await getProposal('proposal')) ||
    fallbackDraft || {
      hotels: [],
      flights: [],
      dynamic: [],
      ilPackages: [],
      transfers: [],
    };
  await saveProposal('proposal', { ...draft, transfers: nextTransfers });
  return nextTransfers;
};

export const loadIdbProposalTransfers = async (): Promise<TransferProposal[]> => {
  const draft = await getProposal('proposal');
  return draft?.transfers || [];
};

export type TripBuilderIdbDrafts = {
  hotels: HotProposal[];
  flights: FlProposal[];
  transfers: TransferProposal[];
};

export const emptyTripBuilderIdbDrafts = (): TripBuilderIdbDrafts => ({
  hotels: [],
  flights: [],
  transfers: [],
});

const defaultIdbProposalDraft = (): Proposal => ({
  hotels: [],
  flights: [],
  dynamic: [],
  ilPackages: [],
  transfers: [],
});

export const loadIdbProposalDrafts = async (): Promise<TripBuilderIdbDrafts> => {
  const draft = await getProposal('proposal');
  return {
    hotels: draft?.hotels || [],
    flights: draft?.flights || [],
    transfers: draft?.transfers || [],
  };
};

export const persistIdbProposalDrafts = async (
  drafts: TripBuilderIdbDrafts,
  fallbackDraft?: Proposal,
) => {
  const draft = (await getProposal('proposal')) || fallbackDraft || defaultIdbProposalDraft();
  await saveProposal('proposal', {
    ...draft,
    hotels: drafts.hotels,
    flights: drafts.flights,
    transfers: drafts.transfers,
  });
  return drafts;
};

const createEmptyFlightSection = (sectionTitle: string): TripPreviewSection => ({
  requirementLabel: 'Mandatory',
  sectionTitle,
  flights: [],
  hotels: [],
  transfers: [],
});

const createEmptyTransferSection = (): TripPreviewSection => ({
  requirementLabel: 'Mandatory',
  flights: [],
  hotels: [],
  transfers: [],
});

export type TripBuilderMiddleSectionPlan = {
  section: TripPreviewSection;
  draftFlights: TripPreviewFlightCard[];
  transitionDay: string;
  fromCity: string;
  toCity: string;
};

export type TripBuilderNodeDraftPlan = {
  node: TripPreviewChainNode;
  draftHotels: TripPreviewHotelCard[];
  draftTransfers: TripPreviewTransferCard[];
  transferSection: TripPreviewSection | null;
  middleSections: TripBuilderMiddleSectionPlan[];
  draftOnlyMiddleSections: TripBuilderMiddleSectionPlan[];
};

export type TripBuilderSlotDraftPlan = {
  slot: TripPreviewSlotView;
  beforeSection: TripPreviewSection | null;
  afterSection: TripPreviewSection | null;
  draftBeforeFlights: TripPreviewFlightCard[];
  draftAfterFlights: TripPreviewFlightCard[];
  nodes: TripBuilderNodeDraftPlan[];
};

export const buildTripBuilderSlotDraftPlans = (
  slots: TripPreviewSlotView[],
  drafts: TripBuilderIdbDrafts,
  enabled: boolean,
): TripBuilderSlotDraftPlan[] =>
  slots.map((slot) => {
    const firstNode = slot.chainNodes[0];
    const lastNode = slot.chainNodes[slot.chainNodes.length - 1];
    const draftBeforeFlights =
      enabled && firstNode
        ? getDraftFlightsForBefore(
            drafts.flights,
            firstNode.checkIn,
            firstNode.city,
            slot.beforeSection?.flights || [],
          )
        : [];
    const draftAfterFlights =
      enabled && lastNode
        ? getDraftFlightsForAfter(
            drafts.flights,
            lastNode.checkOut,
            lastNode.city,
            slot.afterSection?.flights || [],
          )
        : [];
    const beforeSection =
      slot.beforeSection ||
      (draftBeforeFlights.length ? createEmptyFlightSection('Before hotel') : null);
    const afterSection =
      slot.afterSection ||
      (draftAfterFlights.length ? createEmptyFlightSection('After hotel') : null);

    const nodes = slot.chainNodes.map((node, nodeIndex) => {
      const nextNode = slot.chainNodes[nodeIndex + 1];
      const draftTransfers = enabled
        ? getDraftTransfersForSlot(
            drafts.transfers,
            node.city,
            node.checkIn,
            node.checkOut,
            node.transferSection?.transfers || [],
          )
        : [];
      const transferSection =
        node.transferSection || (draftTransfers.length ? createEmptyTransferSection() : null);
      const existingMiddleKeys = new Set(
        node.middleSections.map((section) => section.transitionKey || ''),
      );

      const middleSections = node.middleSections.map((section) => {
        const transitionParts = (section.transitionKey || '').split('|');
        const toNode = slot.chainNodes.find((item) => item.slotKey === transitionParts[1]);
        const transitionDay = toNode?.checkIn || nextNode?.checkIn || '';
        const toCity = toNode?.city || nextNode?.city || '';
        return {
          section,
          draftFlights: enabled
            ? getDraftFlightsForMiddle(
                drafts.flights,
                transitionDay,
                node.city,
                toCity,
                section.flights,
              )
            : [],
          transitionDay,
          fromCity: node.city,
          toCity,
        };
      });

      const draftOnlyMiddleSections =
        enabled && nextNode
          ? (() => {
              const transitionKey = `${node.slotKey}|${nextNode.slotKey}`;
              if (existingMiddleKeys.has(transitionKey)) {
                return [];
              }
              const draftFlights = getDraftFlightsForMiddle(
                drafts.flights,
                nextNode.checkIn,
                node.city,
                nextNode.city,
                [],
              );
              if (!draftFlights.length) {
                return [];
              }
              return [
                {
                  section: {
                    requirementLabel: 'Optional',
                    sectionTitle: 'Between hotels',
                    transitionKey,
                    flights: [],
                    hotels: [],
                    transfers: [],
                  },
                  draftFlights,
                  transitionDay: nextNode.checkIn,
                  fromCity: node.city,
                  toCity: nextNode.city,
                },
              ];
            })()
          : [];

      return {
        node,
        draftHotels: enabled
          ? getDraftHotelsForSlot(
              drafts.hotels,
              node.city,
              node.checkIn,
              node.checkOut,
              node.hotelSection?.hotels || [],
            )
          : [],
        draftTransfers,
        transferSection,
        middleSections,
        draftOnlyMiddleSections,
      };
    });

    return {
      slot,
      beforeSection,
      afterSection,
      draftBeforeFlights,
      draftAfterFlights,
      nodes,
    };
  });

export const buildAllIdbAddReports = (options: {
  hotelsToAdd: HotProposal[];
  flightsToAdd: TripBuilderIdbFlightMatch[];
  transfersToAdd: TripBuilderIdbTransferMatch[];
  slots: TripPreviewSlotView[];
  requirementByRow: Record<string, TripSlotRequirement>;
  hotelImageBySlot: Record<string, string>;
  showCancellationDate: boolean;
  cancellationDateDelta: number;
  language?: string;
}): ReportsList[] => [
  ...buildIdbHotelsAddReports(options.hotelsToAdd, options.slots, {
    requirementByRow: options.requirementByRow,
    hotelImageBySlot: options.hotelImageBySlot,
    showCancellationDate: options.showCancellationDate,
    cancellationDateDelta: options.cancellationDateDelta,
    language: options.language,
  }),
  ...buildIdbFlightsAddReports(options.flightsToAdd, {
    requirementByRow: options.requirementByRow,
  }),
  ...buildIdbTransfersAddReports(options.transfersToAdd, {
    requirementByRow: options.requirementByRow,
  }),
];

const collectIdbIdentities = (...values: Array<string | undefined | null>) =>
  values.filter((value): value is string => !!value);

const getIdbHotelIdentities = (hotel: HotProposal) =>
  collectIdbIdentities(hotel.item?.itemCode, hotel.item?.htuKey, hotel.searchToken);

const getIdbFlightIdentities = (flight: FlProposal) =>
  collectIdbIdentities(flight.item?.id, flight.searchToken);

const getIdbTransferIdentities = (transfer: TransferProposal) =>
  collectIdbIdentities(transfer.forwardItem?.priceKey, transfer.searchToken);

const matchesAnyIdentity = (identities: string[], added: Set<string>) =>
  identities.some((identity) => added.has(identity));

export const filterAddedIdbDrafts = (
  drafts: TripBuilderIdbDrafts,
  added: {
    hotels: HotProposal[];
    flights: TripBuilderIdbFlightMatch[];
    transfers: TripBuilderIdbTransferMatch[];
  },
): TripBuilderIdbDrafts => {
  const hotelIdentities = new Set(added.hotels.flatMap(getIdbHotelIdentities));
  const flightIdentities = new Set(
    added.flights.flatMap(({ flight }) => getIdbFlightIdentities(flight)),
  );
  const transferIdentities = new Set(
    added.transfers.flatMap(({ transfer }) => getIdbTransferIdentities(transfer)),
  );

  return {
    hotels: drafts.hotels.filter(
      (hotel) => !matchesAnyIdentity(getIdbHotelIdentities(hotel), hotelIdentities),
    ),
    flights: drafts.flights.filter(
      (flight) => !matchesAnyIdentity(getIdbFlightIdentities(flight), flightIdentities),
    ),
    transfers: drafts.transfers.filter(
      (transfer) => !matchesAnyIdentity(getIdbTransferIdentities(transfer), transferIdentities),
    ),
  };
};
